/**
 * UNDO-TRASH-LIGHT (L1) — session undo stack, agnostic of framework
 * and domain.
 *
 * Model: each user mutation registers its INVERSE action as a closure
 * (`undo`) re-entering through normal write path (PowerSync/Drizzle) — never
 * direct state manipulation. Undoing is a forward-compensating operation
 * (`git revert` style): coexists with offline and multi-device because
 * the server resolves it like any other write.
 *
 * The stack lives ONLY in UI session memory (lost on reload; that
 * is deliberate: L2 trash exists for recovering persisted deletions).
 *
 * COPYABLE piece as-is to other apps: imports nothing from domain.
 */

export interface UndoEntry {
  /** Short description of what was done (pre-translated), e.g. "Habit deleted". */
  label: string;
  /** Applies inverse mutation through normal write path. */
  undo: () => Promise<void>;
}

export interface UndoStack {
  /** Registers an undoable action. If limit exceeded, oldest is dropped. */
  push: (entry: UndoEntry) => void;
  /**
   * Undoes last action (LIFO). Returns undone entry or `null` if
   * stack is empty. Concurrent calls are serialized (one inverse
   * does not start until previous finishes). If inverse fails, entry
   * is discarded anyway (retrying broken inverse is worse) and error
   * propagates to caller to inform user.
   */
  undoLast: () => Promise<UndoEntry | null>;
  size: () => number;
  /** Empties stack (mandatory on account switch or logout). */
  clear: () => void;
}

export const DEFAULT_UNDO_LIMIT = 50;

export function createUndoStack(opts: { limit?: number } = {}): UndoStack {
  const limit = Math.max(1, opts.limit ?? DEFAULT_UNDO_LIMIT);
  const entries: UndoEntry[] = [];
  // Serializes inverses: repeated Ctrl+Z must not overlap writes.
  let chain: Promise<unknown> = Promise.resolve();

  return {
    push(entry: UndoEntry): void {
      entries.push(entry);
      if (entries.length > limit) {
        entries.shift();
      }
    },
    undoLast(): Promise<UndoEntry | null> {
      const run = chain.then(async () => {
        const entry = entries.pop();
        if (!entry) {
          return null;
        }
        await entry.undo();
        return entry;
      });
      // Chain is never rejected: error only travels to caller of this undoLast.
      chain = run.catch(() => undefined);
      return run;
    },
    size: () => entries.length,
    clear(): void {
      entries.length = 0;
    },
  };
}
